import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';

import {
  CircleNotch,
  Clock,
  Microphone,
  Phone,
  Robot,
  TrendUp,
  WarningCircle
} from '@phosphor-icons/react';
const VoiceAnalyticsDashboard = () => {
  const { activeOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (activeOrganization) {
      fetchAnalytics();
    }
  }, [activeOrganization, timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${BACKEND_URL}/api/analytics/voice/summary?days=${timeRange}`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setAnalytics(result.data);
        } else {
          setAnalytics(null);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        // Handle error detail - could be string, object, or array
        let errorMessage = 'Failed to load voice analytics';
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // FastAPI validation errors format: [{msg: "...", type: "..."}]
            errorMessage = errorData.detail.map(e => {
              if (typeof e === 'string') return e;
              return e.msg || e.type || JSON.stringify(e);
            }).join(', ');
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Error loading analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
        {/* Header */}
        {true && (
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Microphone size={32} className="text-[#6366F1]" />
                <h1 className="text-4xl font-bold text-white">Voice Analytics</h1>
              </div>
              <p className="text-[#A0AEC0]">Track voice agent usage and performance</p>
            </div>

            {/* Time Range Selector */}
            <div className="flex gap-2">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setTimeRange(days)}
                  className={`px-4 py-2 rounded-full font-semibold transition-all ${
                    timeRange === days
                      ? 'bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white'
                      : 'glass text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                  }`}
                >
                  {days} Days
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
            <div className="glass rounded-2xl p-6 mb-8 border-2 border-[#EF4444]">
              <div className="flex items-center gap-3">
                <WarningCircle size={24} className="text-[#EF4444]" />
                <div>
                  <h3 className="text-white font-semibold">Error Loading Analytics</h3>
                  <p className="text-[#A0AEC0]">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!error && !loading && !analytics && (
            <div className="glass rounded-2xl p-12 text-center">
              <Microphone size={64} className="mx-auto mb-6 text-[#6366F1]" />
              <h3 className="text-2xl font-bold text-white mb-3">No Voice Data Yet</h3>
              <p className="text-[#A0AEC0] mb-6">
                Start using voice agents to see analytics here
              </p>
            </div>
          )}

          {analytics && (
            <>
              {/* Stats Cards */}
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass rounded-2xl p-6"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Clock size={24} className="text-[#6366F1]" />
                    <span className="text-[#A0AEC0] text-sm">{timeRange} days</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">
                    {analytics.total_minutes?.toFixed(1) || 0}
                  </p>
                  <p className="text-[#A0AEC0] text-sm">Total Voice Minutes</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="glass rounded-2xl p-6"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Phone size={24} className="text-[#14F195]" />
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">
                    {analytics.total_interactions || 0}
                  </p>
                  <p className="text-[#A0AEC0] text-sm">Voice Interactions</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="glass rounded-2xl p-6"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Microphone size={24} className="text-[#F59E0B]" />
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">
                    {analytics.total_stt_calls || 0}
                  </p>
                  <p className="text-[#A0AEC0] text-sm">STT Calls</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="glass rounded-2xl p-6"
                >
                  <div className="flex items-center justify-between mb-3">
                    <TrendUp size={24} className="text-[#EC4899]" />
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">
                    {analytics.total_tts_calls || 0}
                  </p>
                  <p className="text-[#A0AEC0] text-sm">TTS Calls</p>
                </motion.div>
              </div>

              {/* Minutes Per Day Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass rounded-2xl p-6 mb-8"
              >
                <h3 className="text-xl font-bold text-white mb-6">Voice Minutes Per Day</h3>
                {analytics.daily_minutes && analytics.daily_minutes.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.daily_minutes}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#A0AEC0"
                        tick={{ fill: '#A0AEC0' }}
                      />
                      <YAxis 
                        stroke="#A0AEC0"
                        tick={{ fill: '#A0AEC0' }}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(26, 27, 35, 0.95)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '12px',
                          color: '#fff'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="minutes" 
                        stroke="#6366F1" 
                        strokeWidth={2}
                        dot={{ fill: '#6366F1', r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-[#A0AEC0]">
                    No voice usage data available for this period
                  </div>
                )}
              </motion.div>

              {/* Top Voice Agents */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="glass rounded-2xl p-6"
              >
                <h3 className="text-xl font-bold text-white mb-6">Top Voice Agents</h3>
                {analytics.top_agents && analytics.top_agents.length > 0 ? (
                  <div className="space-y-4">
                    {analytics.top_agents.map((agent, index) => (
                      <div
                        key={agent.agent_id}
                        className="flex items-center justify-between p-4 glass rounded-xl hover:bg-[rgba(255,255,255,0.05)] transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center font-bold text-white">
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">{agent.agent_name || 'Unnamed Agent'}</h4>
                            <p className="text-[#A0AEC0] text-sm">{agent.interactions || 0} interactions</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold">{agent.minutes?.toFixed(1) || 0} min</p>
                          <p className="text-[#A0AEC0] text-sm">voice time</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#A0AEC0]">
                    <Robot size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No voice-enabled agents have been used yet</p>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </div>
  );
};

export default VoiceAnalyticsDashboard;
